/**
 * Snowflake account onboarding and update form.
 */

import React, { FC } from 'react';

import { CloudProviders } from '@ariksa/cloud-account/api';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { Form, FormAction } from 'components/DataEntry/Form';
import { IconTypes } from 'components/Icons';
import { selectSetup } from 'containers/Setup/selectors';
import { actions as setupActions } from 'containers/Setup/slice';

interface Props {
  action?: FormAction;
  accountDetails?: any;
  accountDetailsIsLoading?: boolean;
  onClickCancel?(): void;
}

export const Snowflake: FC<Props> = props => {
  const {
    action = 'Add',
    accountDetails,
    accountDetailsIsLoading,
    onClickCancel,
  } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { onboardAccount } = useSelector(selectSetup);
  const isEdit = action === 'Update';

  const handleReset = () => {
    onClickCancel?.();
    if (!onClickCancel) navigate('/setup/accounts');
  };

  const handleSubmit = data => {
    const { encrypted_password, login, login_url, name } = data;

    if (isEdit) {
      dispatch(
        setupActions.updateCloudAccount({
          q: {
            uuid: accountDetails?.uuid,
            cloudAccountUpdate: {
              name,
              cloud_type: CloudProviders.Snowflake,
              snowflake: { encrypted_password },
            },
          },
          onSuccess: () => navigate('/setup/accounts'),
        }),
      );
      return;
    }

    dispatch(
      setupActions.onboardAccount({
        q: {
          cloudAccountCreateRequest: {
            name,
            cloud_type: CloudProviders.Snowflake,
            snowflake: {
              encrypted_password,
              login,
              login_url,
            },
          },
        },
        onSuccess: () => navigate('/setup/accounts'),
      }),
    );
  };

  return (
    <Form
      title={action + ' Snowflake'}
      iconType={IconTypes.Snowflake}
      isTitleIconFilled={false}
      schema={{
        name: {
          type: 'text',
          label: 'Name',
          placeholder: 'Enter name to be used by Ariksa',
          isRequired: true,
          defaultValue: isEdit ? accountDetails?.name : '',
          autoFocus: true,
        },
        login_url: {
          type: 'text',
          label: 'Login URL',
          isRequired: true,
          defaultValue: isEdit ? accountDetails?.snowflake?.login_url : '',
        },
        login: {
          type: 'text',
          label: 'Login name',
          placeholder: 'Enter login name',
          isRequired: true,
          defaultValue: isEdit ? accountDetails?.snowflake?.login : '',
        },
        encrypted_password: {
          type: 'text',
          label: (isEdit ? 'New ' : '') + 'Password',
          htmlInputType: 'password',
          isRequired: !isEdit,
          placeholder: 'Enter password',
        },
      }}
      buttonOptions={{
        submit: {
          name: 'OK',
          isLoading: onboardAccount.isLoading || accountDetailsIsLoading,
        },
        reset: {
          name: 'CANCEL',
          isVisible: true,
          onClick: handleReset,
        },
      }}
      handleSubmit={handleSubmit}
    />
  );
};
